/**
 * The locks a rentable unit opens and closes with its rentals, as ant maps
 * them in `hardware.config.rental_locks` (the kayak trailer: a kayak maps its
 * wing lock and the shared box). Plain GraphQL over React Query, like the
 * chat hooks: these fields are too new for the pinned schema.graphql, so they
 * stay out of Relay and live only here.
 *
 * `rentalLocks` (a list of the flat RentalLock: the lock's id and display
 * fields, its role and whether it opens at rental start),
 * `HardwareProduct.supportsSharedLocks` and `setHardwareRentalLocks` (which
 * takes `locks: [RentalLockInput!]!`) are not in schema.graphql yet; their
 * shape follows the ant contract by hand (rentalLocksContract.graphql). An
 * ant without them answers the locks query with an error, which this hook
 * reports as `unsupported` so the page shows nothing rather than a broken
 * card. The product flag is read here, by the card's own query, and never by
 * the page's hardware query: an ant without it must still render the rest of
 * the page.
 */

import { useMemo } from 'react';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { postGraphQL, unwrapMutation, MutationPayload } from '../chat/client';

/** What a mapped lock does on the trailer; ant's RentalLockRole enum. */
export type RentalLockRole = 'WING' | 'BOX';

/**
 * One lock of a unit's mapping, as ant embeds it (RentalLock): flat, with
 * the lock's display fields copied in rather than a nested Hardware.
 */
export interface RentalLock {
  hardwareId: string;
  label: string | null;
  vendorId: string | null;
  productName: string | null;
  /** null when unset, as on a mapping saved before roles existed. */
  role: RentalLockRole | null;
  /** Opened when a rental starts; otherwise only on the rider's demand. */
  autoUnlock: boolean;
}

/** One entry of the mapping to save (RentalLockInput), in the order the locks open. */
export interface RentalLockInput {
  hardwareId: string;
  role: RentalLockRole | null;
  autoUnlock: boolean;
}

/** A lock the unit may map: a row of the system's remote-unlock hardware. */
export interface LockCandidate {
  id: string;
  oldId: string | null;
  label: string | null;
  number: number | null;
  vendorId: string | null;
  isOnline: boolean | null;
  hardwareProduct: { name: string | null } | null;
}

/** The mapping entry for a mapped lock: what Save sends back for it unchanged. */
export const toRentalLockInput = ({
  hardwareId,
  role,
  autoUnlock,
}: RentalLockInput): RentalLockInput => ({ hardwareId, role: role ?? null, autoUnlock });

const RENTAL_LOCK_FIELDS = `
  hardwareId
  label
  vendorId
  productName
  role
  autoUnlock
`;

const CANDIDATE_FIELDS = `
  id
  oldId
  label
  number
  vendorId
  isOnline
  hardwareProduct {
    name
  }
`;

export const RENTAL_LOCKS_QUERY = `
  query HardwareRentalLocks($id: ID!) {
    getHardware(id: $id) {
      id
      hardwareProduct {
        supportsSharedLocks
      }
      rentalLocks {
        ${RENTAL_LOCK_FIELDS}
      }
    }
  }
`;

/** ant's page limit for listHardware. */
const CANDIDATES_PAGE = 100;
/** The most locks the picker reads for one system, over several pages. */
export const CANDIDATES_CAP = 500;

/**
 * One page of the hardware in the same system whose product can be opened
 * remotely: what a unit may map. fetchRentalLockCandidates follows the
 * cursor. Every field here is in the pinned schema.graphql.
 */
export const RENTAL_LOCK_CANDIDATES_QUERY = `
  query HardwareRentalLockCandidates($systemId: ID!, $after: String) {
    listHardware(
      filter: {
        systemId: { eq: $systemId }
        hardwareProduct: { supportsRemoteUnlock: { eq: true } }
      }
      first: ${CANDIDATES_PAGE}
      after: $after
    ) {
      edges {
        node {
          ${CANDIDATE_FIELDS}
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const SET_RENTAL_LOCKS_MUTATION = `
  mutation SetHardwareRentalLocks($id: ID!, $input: SetHardwareRentalLocksInput!) {
    setHardwareRentalLocks(id: $id, input: $input) {
      result {
        id
        rentalLocks {
          ${RENTAL_LOCK_FIELDS}
        }
      }
      errors {
        message
        code
        fields
      }
    }
  }
`;

interface RentalLocksData {
  getHardware: {
    id: string;
    hardwareProduct: { supportsSharedLocks: boolean | null } | null;
    rentalLocks: RentalLock[] | null;
  } | null;
}

/** What the locks query answers for a unit: its mapping and its product's opt-in. */
export interface RentalLocksAnswer {
  locks: RentalLock[];
  /**
   * The product's shared-locks flag; null when the product leaves it unset
   * (AshGraphql emits a nullable Boolean) or the unit has no product.
   */
  supportsSharedLocks: boolean | null;
}

interface CandidatesData {
  listHardware: {
    edges: Array<{ node: LockCandidate }> | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
  } | null;
}

export interface RentalLockCandidates {
  locks: LockCandidate[];
  /** The system has more remote-unlock hardware than the cap; the picker says so. */
  truncated: boolean;
}

interface SetRentalLocksData {
  setHardwareRentalLocks: MutationPayload<{
    id: string;
    rentalLocks: RentalLock[] | null;
  }> | null;
}

export const rentalLocksKey = (hardwareId: string) =>
  ['hardwareRentalLocks', hardwareId] as const;

export const rentalLockCandidatesKey = (systemId: string) =>
  ['hardwareRentalLockCandidates', systemId] as const;

export async function fetchRentalLocks(hardwareId: string): Promise<RentalLocksAnswer> {
  const data = await postGraphQL<RentalLocksData>(RENTAL_LOCKS_QUERY, {
    id: hardwareId,
  });
  return {
    locks: data.getHardware?.rentalLocks ?? [],
    supportsSharedLocks: data.getHardware?.hardwareProduct?.supportsSharedLocks ?? null,
  };
}

/** A stable order for the picker: number, then label, then id. */
export function sortCandidates(candidates: LockCandidate[]): LockCandidate[] {
  return [...candidates].sort((a, b) => {
    const an = a.number ?? Number.POSITIVE_INFINITY;
    const bn = b.number ?? Number.POSITIVE_INFINITY;
    if (an !== bn) return an - bn;
    const al = a.label ?? '';
    const bl = b.label ?? '';
    if (al !== bl) return al.localeCompare(bl);
    return a.id.localeCompare(b.id);
  });
}

/**
 * The system's whole list, every unit included: it is cached per system and
 * shared by every unit of it, so the unit itself is left out per observer
 * (in useRentalLocks), never in the cached data. Pages are followed through
 * the relay cursor up to CANDIDATES_CAP distinct rows. Rows are kept by id,
 * so pages that overlap (a row inserted or moved mid-walk) yield one row
 * each, and the walk stops on a page that brings nothing new or whose
 * cursor does not move: a server answering that way would never end.
 */
export async function fetchRentalLockCandidates(
  systemId: string
): Promise<RentalLockCandidates> {
  const byId = new Map<string, LockCandidate>();
  let after: string | null = null;
  /** The cap filled up while ant still had pages to give. */
  let morePages = false;
  for (;;) {
    // Annotated: `after` is reassigned below from this answer, and tsc would
    // otherwise see the page's type depend on itself through the loop (TS7022).
    const data: CandidatesData = await postGraphQL<CandidatesData>(
      RENTAL_LOCK_CANDIDATES_QUERY,
      { systemId, after }
    );
    const page = data.listHardware;
    let added = 0;
    for (const { node } of page?.edges ?? []) {
      if (!byId.has(node.id)) {
        byId.set(node.id, node);
        added += 1;
      }
    }
    const info = page?.pageInfo;
    if (!info?.hasNextPage || !info.endCursor) break;
    if (added === 0 || info.endCursor === after) break;
    if (byId.size >= CANDIDATES_CAP) {
      morePages = true;
      break;
    }
    after = info.endCursor;
  }
  // Truncated whenever rows are left out: pages never read because the cap
  // was full, or rows past the cap in what was read (a last page can overshoot it).
  const truncated = morePages || byId.size > CANDIDATES_CAP;
  const locks = Array.from(byId.values()).slice(0, CANDIDATES_CAP);
  return { locks: sortCandidates(locks), truncated };
}

/**
 * Replaces the unit's mapping with `locks`, in order; [] clears it. Each
 * entry is narrowed to the input's three fields, so a RentalLock read from
 * ant can be passed back as is without its display fields reaching
 * RentalLockInput (which would refuse them).
 */
export async function postRentalLocks(
  hardwareId: string,
  locks: RentalLockInput[]
): Promise<RentalLock[]> {
  const data = await postGraphQL<SetRentalLocksData>(SET_RENTAL_LOCKS_MUTATION, {
    id: hardwareId,
    input: { locks: locks.map(toRentalLockInput) },
  });
  const result = unwrapMutation(data.setHardwareRentalLocks, 'setHardwareRentalLocks');
  return result.rentalLocks ?? [];
}

/**
 * ant does not have the shared-locks feature: Absinthe's validation error for
 * exactly one of the new fields on exactly its type, anywhere in the message
 * (several errors come joined by "; "). An ant with the mapping but without
 * the product flag counts too: the card cannot tell which units opt in, so
 * it shows nothing. So does an ant still on the first contract, whose
 * rentalLocks lists Hardware rather than RentalLock: it refuses the flat
 * selection with "hardwareId" on "Hardware", and until ant serves the flat
 * type the card stays hidden rather than offering a Retry that cannot
 * succeed. So does a mutation that refuses the `locks` input field
 * (an ant still on the id-list input), should it ever answer that way: the
 * save fails with a plain explanation. A resolver error that happens to
 * mention a field, a validation error for another field, or any network, 5xx
 * or forbidden failure is not this and must not hide a feature ant does have.
 */
export function isRentalLocksUnsupportedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /Cannot query field "rentalLocks" on type "Hardware"/.test(message) ||
    /Cannot query field "supportsSharedLocks" on type "HardwareProduct"/.test(message) ||
    /Cannot query field "hardwareId" on type "Hardware"/.test(message) ||
    /Cannot query field "locks" on type "SetHardwareRentalLocksInput"/.test(message)
  );
}

/**
 * Whether a cached answer is ant refusing the field. Such an ant answers the
 * same way every time, so the query is not asked again on mount, focus or
 * reconnect; an explicit Retry, or a reload after an upgrade, still can.
 */
const refusedField = (state: { data?: unknown; error: unknown } | undefined) =>
  state !== undefined &&
  state.data === undefined &&
  isRentalLocksUnsupportedError(state.error);

const refetchUnlessRefused = (query: { state: { data?: unknown; error: unknown } }) =>
  !refusedField(query.state);

/**
 * The units ant refused the field for, per QueryClient (one in the app, one
 * per test). This is the verdict the card reads, and it is written only when
 * a read settles, inside the query function: a success forgets the unit, the
 * refused-field error remembers it, any other failure changes nothing. It is
 * not derived from the query's error or fetch status, which React Query
 * rewrites while a data-less query refetches (error cleared, isFetched kept)
 * or waits offline (paused, not fetching), and it outlives the component, so
 * a remount in either state still shows nothing until ant answers again.
 */
const refusedUnits = new WeakMap<QueryClient, Set<string>>();

const refusedUnitsOf = (client: QueryClient): Set<string> => {
  let units = refusedUnits.get(client);
  if (!units) {
    units = new Set();
    refusedUnits.set(client, units);
  }
  return units;
};

async function fetchRentalLocksRemembering(
  client: QueryClient,
  hardwareId: string
): Promise<RentalLocksAnswer> {
  const refused = refusedUnitsOf(client);
  try {
    const answer = await fetchRentalLocks(hardwareId);
    refused.delete(hardwareId);
    return answer;
  } catch (error) {
    if (isRentalLocksUnsupportedError(error)) refused.add(hardwareId);
    throw error;
  }
}

interface Options {
  /**
   * Whether to read the system's candidate locks now. The picker is opened
   * rarely, so the card passes its dialog state and the list loads on demand.
   */
  candidatesEnabled?: boolean;
}

export function useRentalLocks(
  hardwareId: string | null | undefined,
  systemId: string | null | undefined,
  { candidatesEnabled = true }: Options = {}
) {
  const queryClient = useQueryClient();
  const id = hardwareId ?? '';

  const locksQuery = useQuery({
    queryKey: rentalLocksKey(id),
    queryFn: () => fetchRentalLocksRemembering(queryClient, id),
    enabled: Boolean(id),
    staleTime: 30000,
    retry: false,
    // A refused field stays refused: see refusedField. retryOnMount is a plain
    // boolean, so it is read from the cache at mount; the others take the query.
    retryOnMount: !refusedField(queryClient.getQueryState(rentalLocksKey(id))),
    refetchOnMount: refetchUnlessRefused,
    refetchOnWindowFocus: refetchUnlessRefused,
    refetchOnReconnect: refetchUnlessRefused,
  });

  // React Query keeps the last data through a failed refetch, so a transient
  // error after a load leaves the locks (and an open dialog) in place.
  const loaded = locksQuery.data !== undefined;
  // Fails closed only when ant lacks the field and nothing was ever loaded:
  // then the page shows nothing rather than a broken card. The verdict is the
  // last settled answer (see refusedUnits), never the in-between fetch state.
  // The store is written by the query function right before React Query
  // publishes the result, so the render that follows reads it up to date.
  const unsupported = !loaded && refusedUnitsOf(queryClient).has(id);
  const locks = locksQuery.data?.locks ?? [];
  const supportsSharedLocks = locksQuery.data?.supportsSharedLocks ?? null;
  // Only a real answer hides the card: the product does not opt in and no
  // lock is mapped. A legacy mapping on a product that does not opt in stays
  // visible so it can be cleared; a failure before any answer is not this,
  // so the retry notice is still shown.
  const hidden = loaded && supportsSharedLocks !== true && locks.length === 0;

  const candidatesQuery = useQuery({
    queryKey: rentalLockCandidatesKey(systemId ?? ''),
    queryFn: () => fetchRentalLockCandidates(systemId ?? ''),
    // Only once ant has answered the locks and the unit shows the card, so an
    // unsupported ant, or a unit the feature does not apply to, is asked nothing more.
    enabled: Boolean(id) && Boolean(systemId) && candidatesEnabled && loaded && !hidden,
    staleTime: 30000,
    retry: false,
  });

  // The cached list is the system's; a unit cannot map itself, so each unit
  // drops its own row here rather than in the shared cache.
  const candidates = useMemo(
    () => (candidatesQuery.data?.locks ?? []).filter((lock) => lock.id !== id),
    [candidatesQuery.data, id]
  );

  const mutation = useMutation({
    mutationFn: (entries: RentalLockInput[]) => postRentalLocks(id, entries),
    onSuccess: async (saved) => {
      // The product's flag is not part of the mutation's answer: kept from
      // the last read, then refreshed with the mapping by the invalidation.
      queryClient.setQueryData<RentalLocksAnswer>(rentalLocksKey(id), (previous) => ({
        locks: saved,
        supportsSharedLocks: previous?.supportsSharedLocks ?? null,
      }));
      await queryClient.invalidateQueries({ queryKey: rentalLocksKey(id) });
    },
  });

  return {
    locks,
    /** The product's opt-in as last read; null before any answer or when unset. */
    supportsSharedLocks,
    /** Whether `locks` is a real answer (possibly stale) rather than the empty default. */
    loaded,
    /** ant answered and the unit has nothing to show: product not opted in, nothing mapped. */
    hidden,
    /**
     * Before ant's first answer of any kind. A Retry after a failure is not
     * this (React Query resets a data-less query to pending while it fetches),
     * so the card stays mounted through it; that read shows as `fetching`.
     */
    loading: Boolean(id) && locksQuery.isPending && !locksQuery.isFetched,
    fetching: locksQuery.isFetching,
    unsupported,
    /** A transient failure to load or refresh; null when unsupported (nothing to show). */
    error: unsupported ? null : locksQuery.error,
    retry: locksQuery.refetch,
    candidates,
    candidatesTruncated: candidatesQuery.data?.truncated ?? false,
    /** The first read only; a background refresh keeps the list in place. */
    candidatesLoading: candidatesQuery.isLoading,
    candidatesError: candidatesQuery.error,
    /**
     * Saves the whole mapping, in order, with each lock's role and opening.
     * Rejects with the server's validation message, for the dialog to show.
     */
    setRentalLocks: mutation.mutateAsync,
    saving: mutation.isPending,
    saveError: mutation.error,
    resetSaveError: mutation.reset,
  };
}
